import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { trpc } from '../trpc';
import ReactMarkdown from 'react-markdown';
import { Badge } from './ui/badge';
import { AlertCircle, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
// Import plugin for handling external links in markdown
import rehypeExternalLinks from 'rehype-external-links';

export function MessageNotification() {
  const [open, setOpen] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  const { data: messages, isLoading, refetch } = trpc.messages.getActiveMessages.useQuery();
  const acknowledgeMutation = trpc.messages.acknowledgeMessage.useMutation();
  
  // Filter to only unacknowledged messages and sort by date (newest first)
  const unacknowledgedMessages = messages
    ? [...messages]
        .filter(msg => !msg.acknowledged)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];
  const currentMessage = unacknowledgedMessages[currentMessageIndex];
  const hasMoreMessages = currentMessageIndex < unacknowledgedMessages.length - 1;
  
  // Show dialog when there are unacknowledged messages
  useEffect(() => {
    if (unacknowledgedMessages.length > 0 && !isLoading) {
      setOpen(true);
    }
  }, [unacknowledgedMessages.length, isLoading]);
  
  // Reset index when messages change
  useEffect(() => {
    setCurrentMessageIndex(0);
  }, [messages]);
  
  // Handle closing the dialog and acknowledging the message
  const handleClose = async () => {
    if (currentMessage) {
      try {
        await acknowledgeMutation.mutateAsync({ messageId: currentMessage.id });
        
        // If more messages, show next message
        if (hasMoreMessages) {
          setCurrentMessageIndex(prev => prev + 1);
        } else {
          setOpen(false);
          // Refetch to update list
          refetch();
        }
      } catch (error) {
        console.error('Error acknowledging message:', error);
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  };
  
  // If no messages or still loading, don't render anything
  if (!currentMessage || isLoading) {
    return null;
  }
  
  // Format expiration date if exists
  const expirationText = currentMessage.expiresAt 
    ? `Expires ${formatDistanceToNow(new Date(currentMessage.expiresAt), { addSuffix: true })}`
    : null;
  
  return (
    <Dialog 
      open={open} 
      // Block outside interactions; the only way out is the OK/Next button
      onOpenChange={() => {}}
    >
      <DialogContent 
        className="max-w-xl"
        // Disable the close button and escape/outside interactions
        hasCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-semibold">{currentMessage.title}</DialogTitle>
            <Badge variant={currentMessage.type === 'important' ? 'destructive' : 'secondary'} className="ml-2">
              {currentMessage.type === 'important' 
                ? <><AlertCircle className="h-3 w-3 mr-1" /> Important</> 
                : <><Info className="h-3 w-3 mr-1" /> Info</>
              }
            </Badge>
          </div>
          {expirationText && (
            <DialogDescription className="text-xs text-muted-foreground">
              {expirationText}
            </DialogDescription>
          )}
        </DialogHeader>
        
        <div className="prose prose-sm dark:prose-invert max-w-none mt-2">
          <ReactMarkdown
            rehypePlugins={[
              // Open external links in new tab and add security attributes
              [rehypeExternalLinks, { target: '_blank', rel: ['nofollow', 'noopener', 'noreferrer'] }],
            ]}
            components={{
              // Custom rendering for links to make them blue and styled properly
              a: ({ node, ...props }: any) => (
                <a
                  {...props}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                />
              ),
            }}
          >
            {currentMessage.content}
          </ReactMarkdown>
        </div>
        
        <DialogFooter className="flex justify-between items-center">
          {hasMoreMessages && (
            <div className="text-sm text-muted-foreground">
              {unacknowledgedMessages.length - currentMessageIndex} more message{unacknowledgedMessages.length - currentMessageIndex > 1 ? 's' : ''} to read
            </div>
          )}
          <Button onClick={handleClose}>
            {hasMoreMessages ? 'Next' : 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}